import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const col = collection(db, "caseStudies");

// Admins read everything ordered by `order` asc; nulls floated to end client-side.
export const subscribeAllCaseStudies = (cb) =>
  onSnapshot(query(col, orderBy("createdAt", "desc")), (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => {
      const ao = a.order ?? Number.POSITIVE_INFINITY;
      const bo = b.order ?? Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      return bt - at;
    });
    cb(rows);
  });

const sanitize = (input) => {
  const {
    tag, title, description, color, published, order,
    client, industry, year, location, durationWeeks,
    challenge, approach, outcome,
    results, deliverables, techStack,
    heroImageUrl, gallery,
    testimonial,
    liveUrl, caseUrl,
  } = input;

  const out = {
    tag: tag || "",
    title: title || "",
    description: description || "",
    color: color || "#1f1f1f",
    published: !!published,
  };
  if (order !== undefined && order !== "" && order !== null) out.order = Number(order);
  if (client) out.client = client;
  if (industry) out.industry = industry;
  if (year) out.year = Number(year);
  if (location) out.location = location;
  if (durationWeeks) out.durationWeeks = Number(durationWeeks);
  if (challenge) out.challenge = challenge;
  if (approach) out.approach = approach;
  if (outcome) out.outcome = outcome;
  if (results?.length) out.results = results.filter(Boolean);
  if (deliverables?.length) out.deliverables = deliverables.filter(Boolean);
  if (techStack?.length) out.techStack = techStack.filter(Boolean);
  if (heroImageUrl) out.heroImageUrl = heroImageUrl;
  if (gallery?.length) out.gallery = gallery.filter(Boolean);
  if (testimonial?.quote) {
    out.testimonial = { quote: testimonial.quote, author: testimonial.author || "" };
    if (testimonial.role) out.testimonial.role = testimonial.role;
  }

  // Typed links array. Mirror first web → liveUrl, first case → caseUrl
  // so the mobile app (which only knows liveUrl/caseUrl) keeps working.
  const links = (input.links || [])
    .filter((l) => l && l.url)
    .map((l) => {
      const row = { type: l.type || "web", url: l.url };
      if (l.label) row.label = l.label;
      return row;
    });
  if (links.length) out.links = links;

  const firstWeb = links.find((l) => l.type === "web")?.url || liveUrl;
  const firstCase = links.find((l) => l.type === "case")?.url || caseUrl;
  if (firstWeb) out.liveUrl = firstWeb;
  if (firstCase) out.caseUrl = firstCase;

  return out;
};

export const createCaseStudy = (input) =>
  addDoc(col, {
    ...sanitize(input),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const updateCaseStudy = (id, input) =>
  updateDoc(doc(db, "caseStudies", id), {
    ...sanitize(input),
    updatedAt: serverTimestamp(),
  });

export const setPublished = (id, published) =>
  updateDoc(doc(db, "caseStudies", id), {
    published: !!published,
    updatedAt: serverTimestamp(),
  });

export const deleteCaseStudy = (id) => deleteDoc(doc(db, "caseStudies", id));
